/**
 * Autosave + resume the Add-property wizard's running state to
 * `property_drafts` (COL-529).
 *
 * The whole WizardState is stored as one jsonb blob plus `current_step`, keyed
 * by a draft id minted once per wizard session. RLS scopes the row to its
 * creator, so this works for staff and owner-portal users alike.
 *
 * On mount the hook loads the user's most-recent draft (fail-silent) and
 * exposes it as `resumed` for PropertyNew to restore. The draft's id is adopted
 * into `draftIdRef` only when PropertyNew actually restores it (`adoptResumed`):
 * if the user already started typing, PropertyNew keeps their input and skips
 * the restore, and this wizard's autosave must mint a fresh row rather than
 * overwrite that other draft. Autosave is held until the lookup settles so it
 * can't mint a new row before the restore decision is made.
 *
 * Saves fail silently (status flips to "error", no toast): the draft table is
 * a convenience, and the migration isn't deployed everywhere yet, so a missing
 * table must never block adding a property.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/lib/supabase-types";
import { useAuth } from "@/contexts/AuthContext";
import type { WizardState } from "@/components/admin/v2/wizard/wizardContext";

export type DraftStatus = "idle" | "saving" | "saved" | "error";

const AUTOSAVE_DELAY_MS = 2000;

/**
 * Strip the raw WLAN password out of every apartment before the draft blob is
 * persisted. `wlanPass` is held in wizard memory only and must NEVER reach the
 * unencrypted `property_drafts.state` column (see ChecklistState.wlanPass) —
 * the submit hooks store only a "password provided" marker, never the value.
 * On resume the owner re-enters it. Exported for its own test — a silent
 * regression here leaks a credential at rest.
 */
export function scrubDraftSecrets(state: WizardState): WizardState {
  return {
    ...state,
    apartments: state.apartments.map((apt) =>
      apt.checklist?.wlanPass
        ? { ...apt, checklist: { ...apt.checklist, wlanPass: "" } }
        : apt
    ),
  };
}

/** Has the user entered anything worth keeping a draft of yet? Exported for its
 * own test — the "don't autosave an untouched wizard" rule is easy to break. */
export function isWorthSaving(state: WizardState): boolean {
  return !!(
    state.property.title.trim() ||
    state.property.street_name.trim() ||
    state.property.city.trim() ||
    state.apartments.length > 0
  );
}

export function usePropertyDraft({
  state,
  currentStep,
  enabled,
}: {
  state: WizardState;
  currentStep: number;
  enabled: boolean;
}): {
  enabled: boolean;
  status: DraftStatus;
  saveNow: () => void;
  discard: () => void;
  /** The user's most-recent saved draft, loaded once on mount. `null` until the
   *  lookup finishes (see `resumeChecked`) and whenever there's nothing to
   *  resume. PropertyNew restores it into the wizard exactly once. */
  resumed: { state: WizardState; currentStep: number } | null;
  /** True once the on-mount lookup has settled (found a draft or confirmed
   *  none). PropertyNew waits for this before deciding whether to restore. */
  resumeChecked: boolean;
  /** Adopt the resumed draft's id so later saves upsert that row. PropertyNew
   *  calls this only when it actually applies `resumed` — skipping it (user
   *  already typing) keeps this wizard on a fresh id, so autosave can't
   *  overwrite the other draft. No-op if there was nothing to resume. */
  adoptResumed: () => void;
  discardResumed: () => void;
} {
  const { user } = useAuth();
  const [status, setStatus] = useState<DraftStatus>("idle");
  const [resumed, setResumed] = useState<{
    state: WizardState;
    currentStep: number;
  } | null>(null);
  const [resumeChecked, setResumeChecked] = useState(false);

  // One draft id per wizard session; stable across autosaves. Replaced by the
  // resumed draft's id when (and only when) PropertyNew restores it.
  const draftIdRef = useRef<string>();
  if (!draftIdRef.current) draftIdRef.current = crypto.randomUUID();
  // The id of the draft found on mount, held until PropertyNew decides to adopt
  // it via `adoptResumed`. Never written straight into `draftIdRef`.
  const resumedIdRef = useRef<string>();

  // Latest state/step for the debounced timer and manual save, without
  // re-arming the timer's effect on every keystroke's identity change.
  const latest = useRef({ state, currentStep });
  latest.current = { state, currentStep };

  const canSave = enabled && !!user?.id;

  // Once discarded, no further upsert may run — otherwise the debounced
  // autosave timer that's still armed at submit time fires after the delete and
  // resurrects the row. `inFlightRef` lets discard await a save already past its
  // guard so its upsert can't land after the delete either.
  const discardedRef = useRef(false);
  const inFlightRef = useRef<Promise<void> | null>(null);

  const save = useCallback(async () => {
    if (!canSave || discardedRef.current) return;
    const { state: s, currentStep: step } = latest.current;
    if (!isWorthSaving(s)) return;
    setStatus("saving");
    const run = (async () => {
      const { error } = await supabase.from("property_drafts").upsert({
        id: draftIdRef.current!,
        created_by: user!.id,
        state: scrubDraftSecrets(s) as unknown as Json,
        current_step: step,
      });
      setStatus(error ? "error" : "saved");
    })();
    inFlightRef.current = run;
    await run;
    if (inFlightRef.current === run) inFlightRef.current = null;
  }, [canSave, user]);

  const discard = useCallback(() => {
    if (!enabled) return;
    discardedRef.current = true;
    setStatus("idle");
    // Fire-and-forget delete (leaving the wizard shouldn't wait on it), but
    // sequence it after any in-flight save so the upsert can't resurrect the
    // row. A stray draft is harmless (RLS-scoped, resumable only by its owner).
    const del = () =>
      void supabase
        .from("property_drafts")
        .delete()
        .eq("id", draftIdRef.current!);
    if (inFlightRef.current) void inFlightRef.current.then(del, del);
    else del();
  }, [enabled]);

  // Load the most-recent draft once, on mount. Fail-silent: a missing table or
  // RLS block just means "nothing to resume". The found id is stashed, not
  // adopted — PropertyNew adopts it (adoptResumed) only if it restores.
  const didLoad = useRef(false);
  useEffect(() => {
    if (!canSave || didLoad.current) return;
    didLoad.current = true;
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase
        .from("property_drafts")
        .select("id, state, current_step")
        .eq("created_by", user!.id)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (!error && data) {
        resumedIdRef.current = data.id;
        setResumed({
          state: data.state as unknown as WizardState,
          currentStep: data.current_step ?? 1,
        });
      }
      setResumeChecked(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [canSave, user]);

  // Debounced autosave whenever the state changes and there's something to save.
  // Held until the resume lookup settles so it can't create a second row before
  // adopting an existing draft's id.
  useEffect(() => {
    if (!canSave || !resumeChecked || !isWorthSaving(state)) return;
    const timer = setTimeout(() => void save(), AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
    // `state`/`currentStep` identity drives the debounce; `save` is stable.
  }, [state, currentStep, canSave, resumeChecked, save]);

  const adoptResumed = useCallback(() => {
    if (resumedIdRef.current) draftIdRef.current = resumedIdRef.current;
  }, []);

  // Skip-restore: the user already started typing, so the draft found on mount
  // is stale. Delete it (don't adopt — adopting would overwrite it with the
  // user's unrelated fresh input) and clear the ref, so autosave creates one
  // clean new row under draftIdRef instead of resurrecting the old one on every
  // later "Add property" visit. Fire-and-forget; autosave targets draftIdRef,
  // never this id, so there's nothing to sequence against.
  const discardResumed = useCallback(() => {
    const staleId = resumedIdRef.current;
    if (!staleId) return;
    resumedIdRef.current = undefined;
    void supabase.from("property_drafts").delete().eq("id", staleId);
  }, []);

  return {
    enabled,
    status,
    saveNow: () => void save(),
    discard,
    resumed,
    resumeChecked,
    adoptResumed,
    discardResumed,
  };
}
